'use client'
import type { ColumnDef } from '@tanstack/react-table'

import { safeParseNumber } from '@morpho-org/blue-sdk-viem'
import { InterfaceChainUtils } from '@morpho-org/dapp-core'
import { CellTokenPrice, MarketIdentity, NetworkAvatar as Network } from '@morpho-org/ui-v6/client'
import { Tag } from '@morpho-org/ui-v6/server'
import { formatters, getMarketName } from '@morpho-org/ui-v6/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui'
import {
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createSortedRowModel,
  rowSortingFeature,
  sortFns,
  tableFeatures
} from '@tanstack/react-table'

import CellNetApy from '@/components/common/Cells/CellNetApy'
import MarketPositionManagerClient from '@/components/common/PositionManager/MarketPositionManager/MarketPositionManager.client'
import { WarningIcon } from '@/components/common/WarningIcon'
import { OwnPortfolioOnly } from '@/components/DashboardPage/OwnPortfolioOnly'
import { AVG_APY_DURATION_LABEL } from '@/constants/avgApy'
import { getParsedValue } from '@/helpers/formatters'

import type { LendPositionsTableRow } from './LendPositionsTable.types'

export const columnLabelsLendPositions = {
  network: 'Network',
  market: 'Market',
  lent: 'Lent',
  // Keyed by column id (`netApy`) — CompactTable.Head derives its "Sort by …"
  // label from `columnLabels[column.id]`.
  netApy: 'Rate'
}

export const lendPositionsTableFeatures = tableFeatures({
  columnFilteringFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  rowSortingFeature,
  sortFns,
  sortedRowModel: createSortedRowModel()
})

export type LendPositionsTableFeatures = typeof lendPositionsTableFeatures

type LendPositionsColumnDef = ColumnDef<LendPositionsTableFeatures, LendPositionsTableRow>
export const columnsLendPositions: LendPositionsColumnDef[] = [
  {
    id: 'network',
    minSize: 100,
    size: 104,
    accessorFn: row => InterfaceChainUtils.getMetadata(row.market.chainId).chainIdentifier,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {columnLabelsLendPositions.network}
          </span>
        </TooltipTrigger>
        <TooltipContent>The network of the market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const chainId = row.original.market.chainId
      const chainName = InterfaceChainUtils.getMetadata(chainId).chain.name
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center">
              <Network
                chainId={chainId}
                srcAvatarProps={{
                  size: 'small'
                }}
              />
            </div>
          </TooltipTrigger>
          <TooltipContent>{chainName}</TooltipContent>
        </Tooltip>
      )
    }
  },
  {
    id: 'market',
    size: 240,
    // Same helper as the `MarketIdentity` below, so the sort order matches what the
    // user reads. Composing the name here instead would drop the symbol fallbacks:
    // a collateral token with an empty symbol reads `LOAN | UNKNOWN` but would sort
    // as `LOAN`.
    accessorFn: row =>
      getMarketName({ loanAsset: row.lent.token, collateralAsset: row.collateral.token }),
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground">
            {columnLabelsLendPositions.market}
          </span>
        </TooltipTrigger>
        <TooltipContent>The market you are lending to.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => {
      const loanToken = row.original.lent.token
      const collateralToken = row.original.collateral.token
      const lltv = row.original.market.lltv

      // `market.lltv` is a unit fraction (e.g. 0.86), not a WAD value —
      // `toBigInt` would throw on the non-integer.
      const formattedLltv = formatters.percent
        .digits(2)
        .trailingZero(false)
        .default('-')
        .unit('%')
        .of(safeParseNumber(lltv), 18)

      return (
        <div className="gap-2xs flex items-center">
          {/* LLTV stays in the Tag below: `market.lltv` is a unit fraction, not the
              WAD value `MarketIdentity` expects. */}
          <MarketIdentity loanAsset={loanToken} collateralAsset={collateralToken ?? undefined} />
          <div className="flex items-center gap-2.5">
            <Tooltip>
              <TooltipTrigger asChild>
                <Tag variant="main">{formattedLltv}</Tag>
              </TooltipTrigger>
              <TooltipContent>Liquidation loan to value (LLTV)</TooltipContent>
            </Tooltip>
            <WarningIcon warnings={row.original.collateral.warnings} />
          </div>
        </div>
      )
    }
  },
  {
    id: 'lent',
    size: 180,
    accessorFn: row => row.lent.value,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground ml-3.5">
            {columnLabelsLendPositions.lent}
          </span>
        </TooltipTrigger>
        <TooltipContent>The total amount you have lent to this market.</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <div className="flex flex-row items-center">
        <CellTokenPrice
          token={row.original.lent.token}
          amount={row.original.lent.amount}
          value={getParsedValue(row.original.lent.value, safeParseNumber)}
          withAvatar
        />
      </div>
    )
  },
  {
    id: 'netApy',
    // Fits the CellNetApy hover pill (value + up to 2 reward/points icons +
    // padding + cell indent) so it can't overflow into the next cell and get
    // clipped by its hover background.
    size: 125,
    accessorFn: row => row.netApy.avgNetSupplyApy,
    header: () => (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="text-3xs text-secondary-foreground ml-3.5">
            {columnLabelsLendPositions.netApy}
          </span>
        </TooltipTrigger>
        <TooltipContent>{`Average lend rate for the past ${AVG_APY_DURATION_LABEL}.`}</TooltipContent>
      </Tooltip>
    ),
    cell: ({ row }) => (
      <CellNetApy
        variant="supply"
        performanceFee={0n}
        netApy={row.original.netApy.avgNetSupplyApy}
        apy={row.original.netApy.avgSupplyApy}
        rewards={row.original.netApy.rewards}
        asset={row.original.lent.token}
        merklContext={{
          kind: 'market',
          chainId: row.original.lent.token.chain.id,
          marketId: row.original.market.marketId,
          loanTokenAddress: row.original.lent.token.address
        }}
      />
    )
  },
  {
    id: 'positionManager',
    size: 56,
    header: () => null,
    // Entry point for the lend actions (VRM-5325 / VRM-5326). Same `…` menu as the borrow
    // rows, restricted to the lend side so neither table offers the other side's actions.
    cell: ({ row }) => (
      <OwnPortfolioOnly>
        <MarketPositionManagerClient
          marketId={row.original.market.marketId}
          chainId={row.original.lent.token.chain.id}
          loanAsset={row.original.lent.token}
          hasBorrowPosition={row.original.hasBorrowPosition}
          actionGroup="lend"
        />
      </OwnPortfolioOnly>
    )
  }
]
